
import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogImage?: string;
  schemaJson?: object | object[];
  noIndex?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const SeoHead = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  schemaJson,
  noIndex = false,
}: SeoHeadProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to upsert a meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^\]]+)="([^\]]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Helper to upsert a link tag
    const setLink = (selector: string, attrs: Record<string, string>) => {
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);
    if (noIndex) {
      setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      setMeta(
        'meta[name="robots"]',
        'content',
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      );
    }

    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    setLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });

    // OG tags
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    if (ogImage) setMeta('meta[property="og:image"]', 'content', ogImage);

    // Twitter
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    if (ogImage) setMeta('meta[name="twitter:image"]', 'content', ogImage);

    // Schema JSON-LD
    const SCHEMA_ID = 'seo-head-schema';
    const existing = document.getElementById(SCHEMA_ID);
    if (existing) existing.remove();

    if (schemaJson) {
      const schemas = Array.isArray(schemaJson) ? schemaJson : [schemaJson];
      schemas.forEach((schema, i) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = i === 0 ? SCHEMA_ID : `${SCHEMA_ID}-${i}`;
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Cleanup injected schema scripts on unmount
      const SCHEMA_ID_CLEANUP = 'seo-head-schema';
      document.querySelectorAll(`[id^="${SCHEMA_ID_CLEANUP}"]`).forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonicalPath, ogImage, schemaJson, noIndex]);

  return null;
};

export default SeoHead;
